import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import Machine from "../components/Machine";
import { motion } from "framer-motion";
import apiClient from "../api/client";
import moment from "moment-timezone";
import "moment/locale/it";

moment.locale("it");

export default function Dashboard() {
  const [machines, setMachines] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await apiClient.get("api/dashboard/loadDashboard");
        if (response.ok && response.data) {
          setMachines(response.data.machines);
          setLastSync(response.data.lastSuccessfulSync);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setError("Errore nel caricamento della dashboard");
        setLoading(false);
      }
    };

    fetchDashboardData();
    // Refresh data every minute
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatLastSync = (timestamp) => {
    if (!timestamp) return "Mai";
    const syncTime = moment.tz(timestamp, "Europe/Rome");
    const now = moment().tz("Europe/Rome");
    const diffMinutes = now.diff(syncTime, "minutes");

    if (diffMinutes < 60) {
      return `${diffMinutes} minuti fa`;
    } else if (diffMinutes < 1440) {
      const hours = Math.floor(diffMinutes / 60);
      return `${hours} ore fa`;
    } else {
      return syncTime.format("DD/MM/YYYY HH:mm");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="loading loading-spinner text-red-500"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-red-500 text-xl">{error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen p-6"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl font-bold text-gray-800"
            >
              Dashboard Macchine
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="text-sm text-gray-600 flex items-center gap-2"
            >
              <span className="font-semibold">Ultima sincronizzazione:</span>
              <span
                className={`${
                  !lastSync ||
                  moment()
                    .tz("Europe/Rome")
                    .diff(moment.tz(lastSync, "Europe/Rome"), "minutes") > 5
                    ? "text-red-500"
                    : "text-green-500"
                }`}
              >
                {formatLastSync(lastSync)}
              </span>
            </motion.div>
          </div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {machines.map((machine, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 0 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <Machine
                  name={machine.name}
                  status={machine.status}
                  lastUpdated={machine.lastUpdated}
                  logs={machine.logs}
                  commessa={machine.currentCommessa}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </Layout>
  );
}
